import {useCallback, useEffect, useState} from 'react';
import {connect, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';

import {
  MODULE_DESTINATION,
  MODULE_ROOMS,
  MODULE_DATES,
  MODULE_SPECIAL_RATES,
  VIEW_PANNEL,
  CUSTOM_EVENT_ID,
} from './constants';
import ViewByKey from './components/ViewByKey';
import drupalSettings from './drupalSettings';
import filterChange from './actions/filterChange';
import {pushSearchNavigationClick, pushSearchSubmitError} from '@helpers/gtm';
import { useUrl } from 'js/url';
import {parseUrlToState} from 'js/url/utils';
import submitStrategies from './submitStrategies';
import {pushOpenDatesFilter, pushOpenDestinationFilter} from './customEvents';
import toggleGlobalLoader from 'js/components/Loader/actions/loaderActions';
import {LOADER_SEARCHBOX_SUBMIT} from 'js/components/Loader/constants';

const MainSearchBox = function ({
  view = VIEW_PANNEL,
  modules = [MODULE_DESTINATION, MODULE_ROOMS, MODULE_DATES, MODULE_SPECIAL_RATES],
  activeFilter,
  filterChangeAction,
  toggleGlobalLoaderAction,
  applyOnModuleChange = false,
  submitOnModuleChange = false,
  submitStrategy,
  urlParams,
  ...props
}) {
  const dispatch = useDispatch();
  const urlApi = useUrl();
  const {setParam, applyChanges, discardChanges, setParamImmediate} = urlApi;
  const [loading, setLoading] = useState(false);
  const submitStrategyFunc = submitStrategies[submitStrategy || 'default'];

  const toggleLoader = function (action) {
    setLoading(action);
    toggleGlobalLoaderAction(action, LOADER_SEARCHBOX_SUBMIT);
  };

  const onFormSubmit = useCallback(async function () {
    pushSearchNavigationClick();

    const resp = await submitStrategyFunc(urlApi, dispatch, toggleLoader);
    if (!resp?.success && resp?.field === 'destination') {
      pushOpenDestinationFilter();
      pushSearchSubmitError({
        errorField: 'destination',
        errorType: 'blank',
      });
    }

    if (!resp?.success && resp?.field === 'dates') {
      pushOpenDatesFilter();
      pushSearchSubmitError({
        errorField: 'dateIn',
        errorType: 'blank',
      });
    }

    if (!resp?.redirect) {
      toggleLoader(false);
    }

    return resp;
  }, [dispatch, submitStrategy, urlApi.parameters]);

  const onModuleChange = function (newData, module) {
    setParam(newData);
    if (applyOnModuleChange) {
      applyChanges();
    }
    if (submitOnModuleChange) {
      onFormSubmit();
    }
  };

  const onFormReset = function () {
    discardChanges();
  };

  const isModuleAvailable = useCallback(function (module) {
    return modules.includes(module);
  }, [modules]);

  const onSearchBoxEvent = useCallback(function (e) {
    if (e.detail.type === 'open' && e.detail.id) {
      const isModuleExists = isModuleAvailable(e.detail.id);
      if (isModuleExists) {
        e.preventDefault();
        filterChangeAction('activeFilter', e.detail.id, e.detail.opts);
      }
    }

    if (e.detail.type === 'submit') {
      e.preventDefault();
      onFormSubmit();
    }
  }, [isModuleAvailable, filterChangeAction, onFormSubmit]);

  // Resets temporary UI state (e.g., loading) when the page is restored from the browser’s back/forward cache,
  // Use this function when React effects do NOT re-run after a bfcache restore.
  const handlePageShow = function () {
    toggleLoader(false);
  };

  const prefilledValues = function () {
    const prefilledDataFromLink = drupalSettings?.widgets?.filters?.linkToFirstStep;
    const prefilledDestination = drupalSettings?.search?.destinationsSearchPlaceholder;
    if (prefilledDestination && !urlParams?.q) {
      setParamImmediate({
        q: prefilledDestination,
        source: 'prefilled',
      }, false);
    }

    // Especially for Hotel page, we have to prefilled url parameters with hotel id and query
    if (prefilledDataFromLink) {
      const [queryLocation, queryParameters] = prefilledDataFromLink.split('?')
      const parameters = parseUrlToState(queryParameters)

      setParamImmediate({
        q: parameters.q,
        hotelId: parameters.hotelId,
        source: 'prefilled',
      }, false);
    }
  };

  useEffect(function () {
    prefilledValues();

    window.addEventListener('pageshow', handlePageShow);

    if (activeFilter) {
      filterChangeAction('activeFilter', activeFilter);
    }

    return function cleanup() {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    window.addEventListener(CUSTOM_EVENT_ID, onSearchBoxEvent, false);

    return function cleanup() {
      window.removeEventListener(CUSTOM_EVENT_ID, onSearchBoxEvent, false);
    }
  }, [onSearchBoxEvent]);

  return (
    <ViewByKey
      view={view}
      modules={modules}
      loading={loading}
      onSubmit={onFormSubmit}
      onReset={onFormReset}
      onModuleChange={onModuleChange}
      {...(props || {})}
    />
  )
}

MainSearchBox.propTypes = {
  modules: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(mapModules))),
}

const mapStateToProps = ({url}) => ({
  urlParams: url?.parameters,
});

const mapDispatchToProps = {
  filterChangeAction: filterChange,
  toggleGlobalLoaderAction: toggleGlobalLoader,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MainSearchBox);
